import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Home, Users, UserPlus, User, FileText, CreditCard, Wallet, Gift,
    ClipboardCheck, ClipboardList, Truck, Tag, Ticket, ShoppingCart, Box,
    Ruler, MapPin, Cog, Settings, RefreshCcw, LucideSlidersHorizontal,
    LineChart, Image, ChevronDown,
    SidebarIcon,
    SidebarClose,
    SidebarOpenIcon,
    Search,
    LogOut,
    Bell,
    Layers,
    School,
    Receipt,
    ArrowRightLeft,
    IndianRupeeIcon,
    ReceiptIndianRupee,
    HelpCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getDecryptedUserFromCookie } from './utils/getCookie';
import { useSidebar } from '../SidebarContext';
import MobileBottomNav from './MobileBottom';
import { useSearch } from '../SearchContext';
import userImg from '../Img/100BILLION_ICON.png'
import Ripples from 'react-ripples';


const navItems = [
    {
        title: "Dashboard",
        icon: <Home size={18} />,
        link: "/",
    },
    {
        title: "Teams",
        icon: <Users size={18} />,
        subNav: [
            { name: "Attendance", link: "/attendance" },
            { name: "Tasks", link: "/tasks" },
            { name: "CRM", link: "/crm" },
            { name: "Orders", link: "/orders" },
        ],
    },
    {
        title: "Notifications",
        icon: <Bell size={18} />,
        link: "/notifications",
        hidden: true
    },
    {
        title: "Help & Support",
        icon: <HelpCircle size={18} />,
        link: "/help-support",
        hidden: true
    },
    {
        title: "Settings",
        icon: <Settings size={18} />,
        link: "/setting",
        hidden: true
    }

];

const subNavContainerVariants = {
    open: {
        opacity: 1,
        scaleY: 1,
        transition: {
            duration: 0.3,
            when: "beforeChildren",
            staggerChildren: 0.1,
        },
    },
    closed: {
        opacity: 0,
        scaleY: 0,
        transition: {
            duration: 0.2,
            when: "afterChildren",
        },
    },
};

const subNavItemVariants = {
    open: { opacity: 1, y: 0 },
    closed: { opacity: 0, y: -10 },
};

const SideBar = () => {
    const user = getDecryptedUserFromCookie();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeDropdown, setActiveDropdown] = useState(null);
    const activeRef = useRef(null);
    const { isSidebarOpen, setIsSidebarOpen, toggleSidebar, isMobile, setIsMobile, } = useSidebar();
    const [activeName, setActiveName] = useState("Dashboard");
    const [showSearch, setShowSearch] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { setGlobalSearch } = useSearch();
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredItems, setFilteredItems] = useState([]);


    const flattenNavItems = () => {
        let flat = [];
        navItems.forEach(item => {
            if (item.link) {
                flat.push({ title: item.title, link: item.link });
            }
            if (item.subNav) {
                item.subNav.forEach(sub => {
                    flat.push({ title: sub.name, link: sub.link });
                });
            }
        });
        return flat;
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.trim() === "") {
            setFilteredItems([]);
            return;
        }

        const lower = value.toLowerCase();
        const flatItems = flattenNavItems();
        const filtered = flatItems.filter(item =>
            item.title.toLowerCase().includes(lower)
        );
        setFilteredItems(filtered);
    };

    const handleItemClick = (link) => {
        navigate(link);
        setShowSearch(false);
        setSearchTerm("");
        setFilteredItems([]);
    };
    const filteredNavItems = navItems
        .filter(item => {
            if (item.hidden) return false;

            // If no visibility restriction, allow by default
            const userTypeMatches = !item.visibleFor || item.visibleFor === user?.type;
            const visibleSkMatches = !item.visibleSk || item.visibleSk === user?.category;

            return userTypeMatches && visibleSkMatches;
        })
        .map(item => {
            if (item.subNav) {
                const filteredSubNav = item.subNav.filter(sub => {
                    return !sub.visibleFor || sub.visibleFor === user?.type;
                });
                return { ...item, subNav: filteredSubNav };
            }
            return item;
        })

        .filter(item => {
            // If item had subNav and all were filtered out, remove it
            return !item.subNav || item.subNav.length > 0;
        })




    const toggleDropdown = (title) => {
        setActiveDropdown((prev) => (prev === title ? null : title));
    };

    const handleNavigate = (link) => {
        const activeItem = navItems.find(item =>
            item.link === link || item.subNav?.some(sub => sub.link === link)
        );

        if (activeItem?.title) {
            if (activeItem.subNav) {
                const sub = activeItem.subNav.find(sub => sub.link === link);
                setActiveName(sub?.name || activeItem.title);
            } else {
                setActiveName(activeItem.title);
            }
        }

        navigate(link);

        // ✅ Close sidebar only on mobile
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    };

    const navigateAndSetActiveName = (link) => {
        const match = navItems.find(item => item.link === link);
        if (match) setActiveName(match.title);
        navigate(link);
        if (isMobile) setIsSidebarOpen(false);
    };


    useEffect(() => {
        const currentPath = location.pathname;

        const activeItem = navItems.find(item =>
            item.link === currentPath || item.subNav?.some(sub => sub.link === currentPath)
        );

        if (activeItem) {
            if (activeItem.subNav) {
                const sub = activeItem.subNav.find(sub => sub.link === currentPath);
                setActiveName(sub?.name || activeItem.title);
            } else {
                setActiveName(activeItem.title);
            }
        } else {
            setActiveName("Dashboard"); // fallback
        }
    }, [location.pathname]);

    useEffect(() => {
        if (activeRef.current) {
            activeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (
                isSidebarOpen &&
                isMobile &&
                !e.target.closest(".side-bar-updated") &&
                !e.target.closest(".sidebar-toggle-btn")
            ) {
                setIsSidebarOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isSidebarOpen, isMobile]);


    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setIsSidebarOpen(true); // Always open on desktop
            } else {
                setIsSidebarOpen(false); // Always closed initially on mobile
            }
        };

        handleResize(); // On initial mount

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlelogout = async () => {
        try {
            // Send logout request to backend to clear cookies
            // await fetch(`${urlmain}/login/logout`, {
            //     method: "POST",
            //     credentials: "include", // ✅ ensures cookies are sent
            // });

            // Clear localStorage as well
            localStorage.removeItem("2xgxgshdugyywerw12eccom");
            localStorage.removeItem("2xgxgshdugyywerw12gh");

            // Optional: redirect or reload
            navigate("/login");
            // window.location.href = "/login"; // Or navigate to login
        } catch (err) {
            console.error("Logout failed:", err);
        }
    };



    if (user) {
        console.log("✅ User:", user);
    } else {
        console.log("⚠️ User not found or decryption failed.");
    }

    const handleSwitchAccount = () => {
        window.location.href = "https://100billiontech.com/app.html";
    };


    return (
        <>
            <div className="sidebar-header">
                <div className={`side-bar-updated ${isMobile
                    ? isSidebarOpen ? "open" : "closed"
                    : isSidebarOpen ? "open" : "collapsed"
                    }`} ref={activeRef}>


                    <h1 className="sidebar-company-name">100billion OMS</h1>

                    <ul className="nav-list">
                        {filteredNavItems.map((item, index) => {
                            const isActive = location.pathname === item.link || item.subNav?.some(sub => sub.link === location.pathname);

                            return (
                                <li key={index} className={`nav-item ${isActive ? "active-parent" : ""}`} ref={isActive ? activeRef : null}>
                                    {item.subNav ? (
                                        <>
                                            <Ripples className="nav-button"
                                                onClick={() => toggleDropdown(item.title)}
                                                whileTap={{ scale: 0.97 }}
                                                whileHover={{ backgroundColor: "var(--blue-light)" }} during={1200}>
                                                <button className="flex justify-between items-center w-full">
                                                    <div className="nav-icon-title">
                                                        {item.icon}
                                                        <span>{item.title}</span>
                                                    </div>
                                                    <motion.div
                                                        animate={{ rotate: activeDropdown === item.title ? 180 : 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="sidebar-chevron"
                                                        whileTap={{ scale: 0.97 }}
                                                        whileHover={{ backgroundColor: "var(--blue-light)" }}
                                                    >
                                                        <ChevronDown size={16} />
                                                    </motion.div>
                                                </button>

                                            </Ripples>
                                            <AnimatePresence initial={false}>
                                                {activeDropdown === item.title && (
                                                    <motion.ul
                                                        key="subnav"
                                                        initial="closed"
                                                        animate="open"
                                                        exit="closed"
                                                        variants={subNavContainerVariants}
                                                        className="sub-nav-list open"
                                                        style={{ transformOrigin: "top", paddingLeft: 20 }}
                                                    >
                                                        {item.subNav.map((sub, i) => {
                                                            const isSubActive = location.pathname === sub.link;
                                                            return (
                                                                <motion.li
                                                                    key={i}
                                                                    variants={subNavItemVariants}
                                                                    className={`sub-nav-item ${isSubActive ? "active-sub" : ""}`}
                                                                >
                                                                    <Ripples className="sub-nav-link"
                                                                        onClick={() => handleNavigate(sub.link)} during={1200}>
                                                                        <button>
                                                                            {sub.name}
                                                                        </button>
                                                                    </Ripples>
                                                                </motion.li>
                                                            );
                                                        })}
                                                    </motion.ul>
                                                )}
                                            </AnimatePresence>
                                        </>
                                    ) : (
                                        <Ripples
                                            className={`nav-link ${isActive ? "active-link" : ""}`}
                                            onClick={() => handleNavigate(item.link)}
                                            ref={isActive ? activeRef : null}
                                            whileTap={{ scale: 0.97 }}
                                            whileHover={{ backgroundColor: "var(--blue-light)" }} during={1200}>
                                            <button
                                            >
                                                <div className="nav-icon-title">
                                                    {item.icon}
                                                    <span>{item.title}</span>
                                                </div>
                                            </button>
                                        </Ripples>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    <div className="sidebar-footer">
                        {/* <button className="sidebar-footer-item nav-button" onClick={() => navigateAndSetActiveName("/notifications")} whileTap={{ scale: 0.97 }}
                            whileHover={{ backgroundColor: "var(--blue-light)" }}>
                            <div className="nav-icon-title">
                                <div className="footer-icon-badge">
                                    <Bell size={18} />
                                    <span className="badge-dot"></span>
                                </div>
                                <span>Notifications</span>
                            </div>
                        </button> */}
                        <Ripples during={1200}>
                            <button className="sidebar-footer-item nav-button" onClick={() => navigateAndSetActiveName("/help-support")}>
                                <div className="nav-icon-title">
                                    <HelpCircle size={18} />
                                    <span>Help & Support</span>
                                </div>
                            </button>
                        </Ripples>

                        <Ripples during={1200}>
                            <button className="sidebar-footer-item nav-button" onClick={() => navigateAndSetActiveName("/setting")}>
                                <div className="nav-icon-title">
                                    <Settings size={18} />
                                    <span>Settings</span>
                                </div>
                            </button>
                        </Ripples>

                        <div className="sidebar-profile-logout">
                            <div className="profile-info">
                                <img src={userImg} alt="Profile" className="sidebar-profile-pic" />
                                <span className="profile-name">
                                    {user?.full_name
                                        ? user.full_name.length > 10
                                            ? `${user.full_name.slice(0, 10)}...`
                                            : user.full_name
                                        : ""}
                                </span>

                            </div>

                            <button className="logout-btn-sidebar-updated">
                                <span title="Switch Account" style={{ marginRight: "10px" }} onClick={handleSwitchAccount}>
                                    <ArrowRightLeft size={18} />
                                </span>
                                <span title="Logout Account" onClick={handlelogout}>
                                    <LogOut size={18} />
                                </span>
                            </button>

                        </div>
                    </div>
                </div>
                <div className="header-updated">
                    <div className="toggle-title">
                        <button
                            onClick={toggleSidebar}
                            className="sidebar-toggle-btn"
                            whileTap={{ scale: 0.97 }}
                            whileHover={{ backgroundColor: "var(--blue-light)" }}
                        >
                            {isMobile ? <SidebarIcon /> : (isSidebarOpen ? <SidebarClose /> : <SidebarOpenIcon />)}
                        </button>

                        <h2 className="active-page-title">{activeName}</h2>
                    </div>
                    <div className="search-profile">
                        {/* <div className="header-profile" ref={dropdownRef}>
                            <div className="profile-button" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                <img src="/profile.png" alt="User" />
                                <p>John Doe</p>
                            </div>

                            {isDropdownOpen && (
                                <div className="profile-dropdown">
                                    <button className="dropdown-item" onClick={() => navigate("/setting")}>
                                        <Settings size={16} />
                                        <span>Settings</span>
                                    </button>
                                    <button className="dropdown-item" onClick={handlelogout}>
                                        <LogOut size={16} />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div> */}
                        {/* {showSearch && (
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className="search-input"
                                    autoFocus
                                    onChange={handleSearchChange}
                                />
                            )} */}
                        <div className={`search-container ${showSearch ? "expanded" : ""}`}>
                            <Search
                                className="header-search-icon"
                                onClick={() => setShowSearch(prev => !prev)}
                            />

                            {showSearch && (
                                <div className="search-box">
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="search-input"
                                        autoFocus
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                    {filteredItems.length > 0 && (
                                        <ul className="search-results">
                                            {filteredItems.map((item, idx) => (
                                                <li
                                                    key={idx}
                                                    className="search-result-item"
                                                    onClick={() => handleItemClick(item.link)}
                                                >
                                                    {item.title}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* <MobileBottomNav /> */}
        </>
    );
};

export default SideBar;